// Professional Dashboard Page - inspired by Photo AI design
import { createElement } from '@/utils/dom'
import { dashboardApi, routeApi } from '@/services/api'
import { router } from '@/utils/router'
import { SidebarNavigation } from '@/components/navigation'
import { ColaboradoresComponent } from '@/components/colaboradores'
import { authService } from '@/services/auth'
import { subscriptionService } from '@/services/subscription'
import type { Report, RouteGeneration } from '@/types'

export class DashboardPage {
  private reports: Report[] = []
  private routeGenerations: RouteGeneration[] = []
  private isLoading = true
  private sidebar: SidebarNavigation
  private colaboradoresComponent: ColaboradoresComponent | null = null
  private reportsPageIndex: number = 0
  private isReportsCollapsed: boolean = false
  private static readonly REPORTS_PER_PAGE = 5
  private static readonly REPORTS_STORAGE_KEY = 'dashboard_reports_collapsed'

  constructor() {
    this.sidebar = new SidebarNavigation({
      currentPage: 'dashboard',
      onNavigate: this.handleSidebarNavigation.bind(this)
    })
    // Load collapsed state from localStorage
    this.isReportsCollapsed = localStorage.getItem(DashboardPage.REPORTS_STORAGE_KEY) === 'true'
  }

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'dashboard-page app-layout' })

    // Add sidebar
    page.appendChild(this.sidebar.render())

    // Main content area
    const main = createElement('main', { className: 'main-content' })

    if (this.isLoading) {
      main.appendChild(this.createLoadingState())
      this.loadDashboardData(main)
    } else {
      main.appendChild(this.createDashboardContent())
    }

    page.appendChild(main)
    return page
  }

  private handleSidebarNavigation(page: string): void {
    switch (page) {
      case 'statistics':
        router.navigate('/statistics')
        break
      case 'generate':
        // Trigger route generation modal
        this.showRouteGenerationModal()
        break
      case 'trip-purposes':
        router.navigate('/trip-purposes')
        break
      case 'whats-new':
        router.navigate('/whats-new')
        break
      case 'settings':
        router.navigate('/settings')
        break
      case 'admin':
        router.navigate('/admin')
        break
    }
  }

  private createLoadingState(): HTMLElement {
    const loader = createElement('div', { className: 'loading-state' })
    loader.innerHTML = `
      <div class="container">
        <div style="text-align: center; padding: 4rem 0;">
          <div class="loading"></div>
          <p style="margin-top: 1rem; color: var(--text-secondary);">A carregar o seu dashboard...</p>
        </div>
      </div>
    `
    return loader
  }

  private async loadDashboardData(container: HTMLElement): Promise<void> {
    try {
      console.log('Loading dashboard data...')

      // Refresh user subscription status before loading dashboard
      try {
        await authService.refreshUserData()
        console.log('User subscription data refreshed')
      } catch (error) {
        console.error('Failed to refresh user data, using cached data:', error)
        // Continue with cached data
      }

      // Load all dashboard data in parallel
      const [reports, generations] = await Promise.all([
        dashboardApi.getReports().catch((error) => {
          console.error('Reports API failed:', error)
          return []  // Return empty array instead of mock data for security
        }),
        dashboardApi.getRouteGenerations().catch((error) => {
          console.error('Generations API failed:', error)
          return []  // Return empty array instead of mock data for security
        })
      ])

      console.log('Loaded reports:', reports)
      console.log('Loaded generations:', generations)

      // TEMPORARY FIX: Convert completed generations to reports format if reports are empty
      if (reports.length === 0 && generations.length > 0) {
        console.log('Converting completed generations to reports format')
        this.reports = generations
          .filter(gen => gen.status === 'completed')
          .map(gen => ({
            generation_id: gen.generation_id,
            month: gen.month,
            year: gen.year,
            status: gen.status,
            has_file: true, // Assume completed generations have files
            total_trips: gen.total_trips || 0,
            total_kilometers: gen.total_kilometers || 0,
            created_at: gen.created_at
          }))
      } else {
        this.reports = reports
      }

      this.routeGenerations = generations
      this.isLoading = false

      // Replace loading state with actual content
      container.innerHTML = ''
      container.appendChild(this.createDashboardContent())

    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      // Show empty dashboard instead of mock data for security
      this.reports = []
      this.routeGenerations = []
      this.isLoading = false

      container.innerHTML = ''
      container.appendChild(this.createDashboardContent())
    }
  }

  private createDashboardContent(): HTMLElement {
    const content = createElement('div', { className: 'dashboard-content' })

    const hasActiveSubscription = authService.hasActiveSubscription()

    content.innerHTML = `
      <div class="container">
        <!-- Header -->
        <div class="dashboard-header">
          <div>
            <h1>Dashboard</h1>
            <p style="color: var(--text-secondary);">Acompanhe as simulações de rotas e análises de padrões de viagem</p>
            ${this.createSubscriptionStatusBadge()}
          </div>
          ${hasActiveSubscription ? `
            <button class="btn btn-primary" id="generate-report-btn">
              Gerar Rota
            </button>
          ` : `
            <button class="btn btn-cta-flash" id="upgrade-subscription-btn">
              <i class="fa-solid fa-rocket"></i>
              Comece já a simular relatórios →
            </button>
          `}
        </div>

        <!-- Colaboradores Section (all users - needed for free generation) -->
        <div id="colaboradores-container"></div>

        <!-- Recent Reports (moved below header) -->
        ${this.createReportsSection()}

        <!-- Route Generations Active (moved to bottom) -->
        ${this.createRouteGenerationsActiveSection()}
      </div>
    `

    // Add event handlers
    this.addEventHandlers(content)
    this.addRouteGenerationEventListener()

    // Render colaboradores section for all users (needed for free generation)
    this.renderColaboradoresSection(content)

    return content
  }

  private async renderColaboradoresSection(content: HTMLElement): Promise<void> {
    const container = content.querySelector('#colaboradores-container')
    if (!container) return

    this.colaboradoresComponent = new ColaboradoresComponent({
      onColaboradorChange: () => {
        // Optionally refresh other dashboard data when colaboradores change
        console.log('Colaboradores changed')
      }
    })

    try {
      const colaboradoresEl = await this.colaboradoresComponent.render()
      container.appendChild(colaboradoresEl)
    } catch (error) {
      console.error('Failed to render colaboradores section:', error)
    }
  }

  private createSubscriptionStatusBadge(): string {
    const user = authService.getUser()
    if (!user) return ''

    return ''
  }

  private createRouteGenerationsActiveSection(): string {
    const recentGenerations = this.routeGenerations.slice(0, 3) // Show last 3 generations

    if (recentGenerations.length === 0) {
      return `
        <div class="route-generations-section">
          <div class="section-header">
            <h2>Gerações de Rotas Ativas</h2>
          </div>
          <div class="empty-generations">
            <div style="text-align: center; padding: 2rem 0; color: var(--text-secondary);">
              <div style="font-size: 2.5rem; margin-bottom: 1rem;">🚗</div>
              <h3>Nenhuma geração ativa</h3>
              <p>Inicie uma nova geração de rotas para começar</p>
              <button class="btn btn-primary" id="start-generation-btn" style="margin-top: 1rem;">
                Gerar Primeira Rota
              </button>
            </div>
          </div>
        </div>
      `
    }

    return `
      <div class="route-generations-section">
        <div class="section-header">
          <h2>Gerações de Rotas Ativas</h2>
          <button class="btn btn-outline btn-sm" id="new-generation-btn">
            + Nova Geração
          </button>
        </div>
        <div class="generations-grid">
          ${recentGenerations.map(generation => this.createGenerationCard(generation)).join('')}
        </div>
      </div>
    `
  }



  private createReportsSection(): string {
    const totalReports = this.reports.length
    const totalPages = Math.ceil(totalReports / DashboardPage.REPORTS_PER_PAGE)
    const startIndex = this.reportsPageIndex * DashboardPage.REPORTS_PER_PAGE
    const endIndex = startIndex + DashboardPage.REPORTS_PER_PAGE
    const visibleReports = this.reports.slice(startIndex, endIndex)

    const canGoUp = this.reportsPageIndex > 0
    const canGoDown = this.reportsPageIndex < totalPages - 1
    const showNavigation = totalReports > DashboardPage.REPORTS_PER_PAGE
    const collapseClass = this.isReportsCollapsed ? 'collapsed' : 'expanded'
    const collapseIcon = this.isReportsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'

    return `
      <div class="reports-section">
        <div class="section-header section-toggle-header" id="reports-toggle">
          <h2><i class="fa-solid fa-chart-line"></i> Simulações Recentes</h2>
          <div class="section-header-right">
            ${showNavigation ? `
              <div class="reports-navigation">
                <span class="reports-page-info">${startIndex + 1}-${Math.min(endIndex, totalReports)} de ${totalReports}</span>
                <button class="btn btn-icon btn-sm" id="reports-nav-up" ${!canGoUp ? 'disabled' : ''} title="Anteriores">
                  <i class="fa-solid fa-chevron-up"></i>
                </button>
                <button class="btn btn-icon btn-sm" id="reports-nav-down" ${!canGoDown ? 'disabled' : ''} title="Seguintes">
                  <i class="fa-solid fa-chevron-down"></i>
                </button>
              </div>
            ` : ''}
            <button class="btn btn-icon btn-sm section-collapse-btn" id="reports-collapse-btn" title="${this.isReportsCollapsed ? 'Expandir' : 'Minimizar'}">
              <i class="fa-solid ${collapseIcon}"></i>
            </button>
          </div>
        </div>

        <div class="section-content-wrapper ${collapseClass}" id="reports-content">
          <div class="reports-table">
            <div class="table-header">
              <div>Simulação</div>
              <div>Estado</div>
              <div>Viagens</div>
              <div>Distância</div>
              <div>Criado</div>
              <div>Ações</div>
            </div>

            ${visibleReports.length === 0 ? this.createEmptyState() :
        visibleReports.map(report => this.createReportRow(report)).join('')}
          </div>
        </div>
      </div>
    `
  }



  private createGenerationCard(generation: RouteGeneration): string {
    const status = this.getStatusBadge(generation.status)
    const progress = generation.status === 'completed' ? 100 :
      generation.status === 'processing' ? 65 : 0

    return `
      <div class="generation-card">
        <div class="generation-header">
          <h4>${this.formatReportName({ month: generation.month, year: generation.year } as any)}</h4>
          ${status}
        </div>
        <div class="generation-details">
          <p>Objetivo: €${generation.target_amount}</p>
          ${generation.total_cost ? `<p>Gerado: €${this.formatCurrency(generation.total_cost)}</p>` : ''}
          ${generation.total_trips ? `<p>Viagens: ${generation.total_trips}</p>` : ''}
        </div>
        <div class="generation-progress">
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${progress}%"></div>
          </div>
        </div>
      </div>
    `
  }

  private createReportRow(report: Report): string {
    const status = this.getStatusBadge(report.status)
    const date = new Date(report.created_at).toLocaleDateString('pt-PT')

    return `
      <div class="table-row mobile-card" data-report-id="${report.generation_id}">
        <div class="table-cell" data-label="Simulação">
          <strong>${this.formatReportName(report)}</strong>
        </div>
        <div class="table-cell" data-label="Estado">${status}</div>
        <div class="table-cell" data-label="Viagens">${report.total_trips || '-'}</div>
        <div class="table-cell" data-label="Distância">${report.total_kilometers ? this.formatKilometers(report.total_kilometers) : '-'}</div>
        <div class="table-cell" data-label="Criado">${date}</div>
        <div class="table-cell table-actions" data-label="Ações">
        ${report.has_file ?
        `<div class="action-buttons-group">
          <button class="btn btn-sm btn-icon download-btn" data-id="${report.generation_id}" title="Mapa KM (Excel)">
            <i class="fa-solid fa-download"></i>
          </button>
          <button class="btn btn-sm btn-icon download-ajudas-btn" data-id="${report.generation_id}" title="Ajudas de Custo (Excel)">
            <i class="fa-solid fa-euro-sign"></i>
          </button>
          <button class="btn btn-sm btn-icon delete-btn" data-id="${report.generation_id}" title="Eliminar Simulação">
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>` :
        '<span style="color: var(--text-muted);">A processar...</span>'
      }
        </div>
      </div>
    `
  }

  private createEmptyState(): string {
    return `
      <div class="empty-state">
        <div style="text-align: center; padding: 3rem 0; color: var(--text-secondary);">
          <div style="font-size: 3rem; margin-bottom: 1rem;">📊</div>
          <h3>Ainda não há simulações</h3>
          <p>Simule a sua primeira rota empresarial para começar</p>
          <button class="btn btn-primary" id="first-report-btn" style="margin-top: 1rem;">
          Criar Primeira Simulação
          </button>
        </div>
      </div>
    `
  }



  private addEventHandlers(content: HTMLElement): void {
    // Generate new report button
    const generateBtn = content.querySelector('#generate-report-btn') as HTMLButtonElement
    generateBtn?.addEventListener('click', () => {
      if (authService.canAccessPremiumFeatures()) {
        this.showRouteGenerationModal()
      } else {
        this.showUpgradeModal()
      }
    })

    // Subscription management buttons
    const upgradeBtn = content.querySelector('#upgrade-subscription-btn') as HTMLButtonElement
    upgradeBtn?.addEventListener('click', () => {
      router.navigate('/pricing')
    })

    const manageLink = content.querySelector('#manage-subscription-link') as HTMLAnchorElement
    manageLink?.addEventListener('click', async (e) => {
      e.preventDefault()
      await this.handleManageSubscription()
    })

    // Route generation buttons from active section
    const newGenerationBtn = content.querySelector('#new-generation-btn') as HTMLButtonElement
    newGenerationBtn?.addEventListener('click', () => {
      if (authService.canAccessPremiumFeatures()) {
        this.showRouteGenerationModal()
      } else {
        this.showUpgradeModal()
      }
    })

    const startGenerationBtn = content.querySelector('#start-generation-btn') as HTMLButtonElement
    startGenerationBtn?.addEventListener('click', () => {
      if (authService.canAccessPremiumFeatures()) {
        this.showRouteGenerationModal()
      } else {
        this.showUpgradeModal()
      }
    })

    // Download buttons - Mapa KM
    content.querySelectorAll('.download-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const reportId = parseInt(btn.getAttribute('data-id') || '0')
        await this.downloadReport(reportId, btn as HTMLButtonElement)
      })
    })

    // Download buttons - Ajudas de Custo
    content.querySelectorAll('.download-ajudas-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const reportId = parseInt(btn.getAttribute('data-id') || '0')
        await this.downloadAjudasCusto(reportId, btn as HTMLButtonElement)
      })
    })

    // Delete buttons
    content.querySelectorAll('.delete-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const reportId = parseInt(btn.getAttribute('data-id') || '0')
        const report = this.reports.find(r => r.generation_id === reportId)
        if (report) {
          await this.showDeleteConfirmationModal(report)
        }
      })
    })

    // First report button (empty state)
    const firstReportBtn = content.querySelector('#first-report-btn') as HTMLButtonElement
    firstReportBtn?.addEventListener('click', () => {
      if (authService.canAccessPremiumFeatures()) {
        this.showRouteGenerationModal()
      } else {
        this.showUpgradeModal()
      }
    })

    // Reports navigation (up/down pagination)
    const navUpBtn = content.querySelector('#reports-nav-up') as HTMLButtonElement
    const navDownBtn = content.querySelector('#reports-nav-down') as HTMLButtonElement

    navUpBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      if (this.reportsPageIndex > 0) {
        this.reportsPageIndex--
        this.refreshReportsSection(content)
      }
    })

    navDownBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      const totalPages = Math.ceil(this.reports.length / DashboardPage.REPORTS_PER_PAGE)
      if (this.reportsPageIndex < totalPages - 1) {
        this.reportsPageIndex++
        this.refreshReportsSection(content)
      }
    })

    // Reports section collapse/expand toggle
    const collapseBtn = content.querySelector('#reports-collapse-btn') as HTMLButtonElement
    collapseBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.toggleReportsCollapse(content)
    })
  }

  private toggleReportsCollapse(content: HTMLElement): void {
    this.isReportsCollapsed = !this.isReportsCollapsed

    // Persist to localStorage
    localStorage.setItem(DashboardPage.REPORTS_STORAGE_KEY, String(this.isReportsCollapsed))

    const contentWrapper = content.querySelector('#reports-content')
    const collapseBtn = content.querySelector('#reports-collapse-btn')

    if (contentWrapper) {
      contentWrapper.classList.toggle('collapsed', this.isReportsCollapsed)
      contentWrapper.classList.toggle('expanded', !this.isReportsCollapsed)
    }

    if (collapseBtn) {
      const icon = collapseBtn.querySelector('i')
      if (icon) {
        icon.className = `fa-solid ${this.isReportsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`
      }
      collapseBtn.setAttribute('title', this.isReportsCollapsed ? 'Expandir' : 'Minimizar')
    }
  }

  private refreshReportsSection(content: HTMLElement): void {
    const reportsSection = content.querySelector('.reports-section')
    if (reportsSection) {
      const newSection = document.createElement('div')
      newSection.innerHTML = this.createReportsSection()
      const newReportsSection = newSection.firstElementChild as HTMLElement
      reportsSection.replaceWith(newReportsSection)

      // Re-attach event handlers for the new section
      this.attachReportsEventHandlers(content)
    }
  }

  private attachReportsEventHandlers(content: HTMLElement): void {
    // Download buttons - Mapa KM
    content.querySelectorAll('.download-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const reportId = parseInt(btn.getAttribute('data-id') || '0')
        await this.downloadReport(reportId, btn as HTMLButtonElement)
      })
    })

    // Download buttons - Ajudas de Custo
    content.querySelectorAll('.download-ajudas-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const reportId = parseInt(btn.getAttribute('data-id') || '0')
        await this.downloadAjudasCusto(reportId, btn as HTMLButtonElement)
      })
    })

    // Delete buttons
    content.querySelectorAll('.delete-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation()
        const reportId = parseInt(btn.getAttribute('data-id') || '0')
        const report = this.reports.find(r => r.generation_id === reportId)
        if (report) {
          await this.showDeleteConfirmationModal(report)
        }
      })
    })

    // Navigation buttons
    const navUpBtn = content.querySelector('#reports-nav-up') as HTMLButtonElement
    const navDownBtn = content.querySelector('#reports-nav-down') as HTMLButtonElement

    navUpBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      if (this.reportsPageIndex > 0) {
        this.reportsPageIndex--
        this.refreshReportsSection(content)
      }
    })

    navDownBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      const totalPages = Math.ceil(this.reports.length / DashboardPage.REPORTS_PER_PAGE)
      if (this.reportsPageIndex < totalPages - 1) {
        this.reportsPageIndex++
        this.refreshReportsSection(content)
      }
    })

    // Collapse button
    const collapseBtn = content.querySelector('#reports-collapse-btn') as HTMLButtonElement
    collapseBtn?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.toggleReportsCollapse(content)
    })
  }

  private async handleManageSubscription(): Promise<void> {
    try {
      const portalSession = await subscriptionService.createCustomerPortalSession()
      window.open(portalSession.url, '_blank')
    } catch (error) {
      console.error('Failed to create customer portal session:', error)
      this.showErrorMessage('Falha ao abrir o portal de gestão de subscrição. Tente novamente.')
    }
  }

  private showSubscriptionRequiredModal(_message: string): void {
    // Direct redirect to pricing page
    router.navigate('/pricing')
  }

  private showUpgradeModal(): void {
    // Check if modal already exists and remove it
    const existingModal = document.querySelector('.modal-overlay')
    if (existingModal) {
      existingModal.remove()
    }

    // Create upgrade modal
    const modal = createElement('div', { className: 'modal-overlay' })
    modal.innerHTML = `
      <div class="modal-content upgrade-modal">
        <div class="modal-header">
          <h2>🚀 Atualizar Subscrição</h2>
          <button class="modal-close" id="close-modal">×</button>
        </div>
        
        <div class="upgrade-modal-content">
          <div class="upgrade-icon">
            <div style="font-size: 4rem; color: var(--primary-color);">📊</div>
          </div>
          
          <h3>Acesso Premium Necessário</h3>
          <p>Para simular rotas empresariais, precisa de uma subscrição ativa.</p>
          
          <div class="premium-features">
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              Simulações de rota ilimitadas
            </div>
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              Exportação profissional para Excel
            </div>
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              Cálculo automático de distâncias
            </div>
            <div class="feature-item">
              <span class="feature-icon">✓</span>
              Suporte prioritário por email
            </div>
          </div>
          
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" id="cancel-upgrade">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" id="view-plans-btn">
              Ver Planos de Subscrição →
            </button>
          </div>
        </div>
      </div>
    `

    // Add modal to page
    document.body.appendChild(modal)

    // Add event handlers
    const closeBtn = modal.querySelector('#close-modal') as HTMLButtonElement
    const cancelBtn = modal.querySelector('#cancel-upgrade') as HTMLButtonElement
    const viewPlansBtn = modal.querySelector('#view-plans-btn') as HTMLButtonElement

    const closeModal = () => {
      modal.remove()
    }

    closeBtn.addEventListener('click', closeModal)
    cancelBtn.addEventListener('click', closeModal)
    viewPlansBtn.addEventListener('click', () => {
      closeModal()
      router.navigate('/pricing')
    })

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal()
      }
    })

    // Close on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)
  }

  private addRouteGenerationEventListener(): void {
    // Listen for sidebar route generation events
    document.addEventListener('sidebar:generate-route', () => {
      this.showRouteGenerationModal()
    })
  }

  private async downloadReport(reportId: number, button: HTMLButtonElement): Promise<void> {
    const originalIcon = button.innerHTML
    button.disabled = true

    // Show progress toast
    const progressToast = this.showProgressToast('A iniciar exportação...')

    try {
      // Use async export with progress callback
      await routeApi.downloadExcelAsync(reportId, (progress: number, status: string) => {
        // Update button with progress percentage
        button.innerHTML = `<span style="font-size: 0.7rem;">${progress}%</span>`
        button.title = `A exportar... ${progress}%`

        // Update progress toast
        this.updateProgressToast(progressToast, progress, status)
      })

      // Show success message
      this.closeProgressToast(progressToast)
      this.showSuccessMessage('Relatório transferido com sucesso!')
    } catch (error) {
      console.error('Download failed:', error)
      this.closeProgressToast(progressToast)
      const errorMessage = error instanceof Error ? error.message : 'Falha ao transferir relatório. Tente novamente, por favor.'
      this.showErrorMessage(errorMessage)
    } finally {
      button.innerHTML = originalIcon
      button.disabled = false
      button.title = 'Mapa KM (Excel)'
    }
  }

  private async downloadAjudasCusto(reportId: number, button: HTMLButtonElement): Promise<void> {
    const originalIcon = button.innerHTML
    button.disabled = true

    // Show progress toast (same as Mapa KM export)
    const progressToast = this.showProgressToast('A iniciar exportação de Ajudas de Custo...')

    try {
      // Use async export with progress callback
      await routeApi.downloadAjudasCustoAsync(reportId, (progress: number, status: string) => {
        // Update button with progress percentage
        button.innerHTML = `<span style="font-size: 0.7rem;">${progress}%</span>`
        button.title = `A exportar... ${progress}%`

        // Update progress toast
        this.updateProgressToast(progressToast, progress, status)
      })

      // Show success message
      this.closeProgressToast(progressToast)
      this.showSuccessMessage('Ajudas de Custo transferidas com sucesso!')
    } catch (error) {
      console.error('Download Ajudas de Custo failed:', error)
      this.closeProgressToast(progressToast)
      const errorMessage = error instanceof Error ? error.message : 'Falha ao transferir Ajudas de Custo. Tente novamente, por favor.'
      this.showErrorMessage(errorMessage)
    } finally {
      button.innerHTML = originalIcon
      button.disabled = false
      button.title = 'Ajudas de Custo (Excel)'
    }
  }


  /**
   * Show a confirmation modal before deleting a report/simulation
   */
  private async showDeleteConfirmationModal(report: Report): Promise<void> {
    // Check if modal already exists and remove it
    const existingModal = document.querySelector('.modal-overlay')
    if (existingModal) {
      existingModal.remove()
    }

    const reportName = this.formatReportName(report)

    // Create delete confirmation modal
    const modal = createElement('div', { className: 'modal-overlay' })
    modal.innerHTML = `
      <div class="modal-content delete-confirmation-modal" style="max-width: 450px;">
        <div class="modal-header">
          <h2>🗑️ Eliminar Simulação</h2>
          <button class="modal-close" id="close-modal">×</button>
        </div>
        
        <div class="delete-modal-content" style="padding: 1.5rem;">
          <div style="text-align: center; margin-bottom: 1.5rem;">
            <div style="font-size: 3.5rem; color: var(--danger-color, #dc3545);">⚠️</div>
          </div>
          
          <p style="text-align: center; margin-bottom: 1rem;">
            Tem a certeza que pretende eliminar a simulação <strong>${reportName}</strong>?
          </p>
          
          <div style="background: var(--bg-secondary, rgba(0,0,0,0.1)); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
            <p style="margin: 0; font-size: 0.9rem; color: var(--text-secondary);">
              <i class="fa-solid fa-info-circle"></i>
              Esta ação é <strong>irreversível</strong>. Todas as viagens associadas a esta simulação serão eliminadas permanentemente.
            </p>
          </div>
          
          <div class="modal-actions" style="display: flex; gap: 1rem; justify-content: flex-end;">
            <button type="button" class="btn btn-outline" id="cancel-delete">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" id="confirm-delete">
              <i class="fa-solid fa-trash"></i>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    `

    // Add modal to page
    document.body.appendChild(modal)

    // Add event handlers
    const closeBtn = modal.querySelector('#close-modal') as HTMLButtonElement
    const cancelBtn = modal.querySelector('#cancel-delete') as HTMLButtonElement
    const confirmBtn = modal.querySelector('#confirm-delete') as HTMLButtonElement

    const closeModal = () => {
      modal.remove()
    }

    closeBtn.addEventListener('click', closeModal)
    cancelBtn.addEventListener('click', closeModal)

    confirmBtn.addEventListener('click', async () => {
      closeModal()
      await this.handleDeleteReport(report.generation_id)
    })

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal()
      }
    })

    // Close on escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
        document.removeEventListener('keydown', handleEscape)
      }
    }
    document.addEventListener('keydown', handleEscape)
  }

  /**
   * Handle the actual deletion of a report after confirmation
   */
  private async handleDeleteReport(reportId: number): Promise<void> {
    try {
      console.log('Deleting report:', reportId)

      const result = await routeApi.deleteGeneration(reportId)

      if (result.success) {
        // Remove from local array
        this.reports = this.reports.filter(r => r.generation_id !== reportId)
        this.routeGenerations = this.routeGenerations.filter(g => g.generation_id !== reportId)

        // Refresh the dashboard content
        const mainContent = document.querySelector('.main-content')
        if (mainContent) {
          mainContent.innerHTML = ''
          mainContent.appendChild(this.createDashboardContent())
        }

        this.showSuccessMessage('Simulação eliminada com sucesso!')
      } else {
        this.showErrorMessage(result.message || 'Falha ao eliminar simulação')
      }
    } catch (error) {
      console.error('Delete report failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Falha ao eliminar simulação. Tente novamente, por favor.'
      this.showErrorMessage(errorMessage)
    }
  }


  private async showRouteGenerationModal(): Promise<void> {
    // Check access first before showing modal
    try {
      const accessCheck = await routeApi.checkGenerationAccess()

      if (!accessCheck.success || accessCheck.data?.redirect_to_pricing) {
        // User needs subscription - redirect to pricing
        this.showSubscriptionRequiredModal(accessCheck.message || "Subscrição requerida para gerar relatórios")
        return
      }
    } catch (error) {
      console.error('Failed to check generation access:', error)
      // On error, show upgrade modal as safe fallback
      this.showUpgradeModal()
      return
    }

    // Check if modal already exists and remove it
    const existingModal = document.querySelector('.modal-overlay')
    if (existingModal) {
      existingModal.remove()
    }

    // Create modal overlay
    const modal = createElement('div', { className: 'modal-overlay' })
    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <h2>Simular Novas Rotas Empresariais</h2>
          <button class="modal-close" id="close-modal">×</button>
        </div>
        
        <form class="route-form" id="route-generation-form">
          <div class="form-group">
            <label for="target-amount" class="form-label">
            Valor a Atingir (EUR) <span class="required">*</span>
            </label>
            <input 
            type="text" 
            id="target-amount" 
            name="target_amount"
            class="form-input" 
            placeholder="2096.40"
            inputmode="decimal"
            pattern="[0-9]*[.,]?[0-9]{0,2}"
            required
            />
            <small class="form-help">€20 - €4.000 (máx. ~10.000km/mês, baseado em horário 6h-22h). Deve ser múltiplo de €0,40.</small>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="month" class="form-label">
                Mês <span class="required">*</span>
              </label>
              <select id="month" name="month" class="form-input" required>
                ${this.generateMonthOptions()}
              </select>
            </div>

            <div class="form-group">
              <label for="year" class="form-label">
                Ano <span class="required">*</span>
              </label>
              <select id="year" name="year" class="form-input" required>
                ${this.generateYearOptions()}
              </select>
            </div>
          </div>

          <div class="form-group">
            <div class="disclaimer-note">
              <strong>Nota:</strong> O sistema calcula automaticamente a distribuição ideal de viagens para atingir o valor pretendido, ajustando o raio, horários (6h-22h) e dias úteis. Em caso de necessidade, pode incluir sábados e domingos.
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-outline" id="cancel-btn">
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary" id="generate-btn">
            <span class="btn-text">Simular Rotas</span>
              <span class="btn-loading hidden">
                <div class="loading-small"></div>
                A processar...
              </span>
            </button>
          </div>
        </form>
      </div>
    `

    // Add modal to page
    document.body.appendChild(modal)

    // Focus on first input
    const firstInput = modal.querySelector('#target-amount') as HTMLInputElement
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100)
    }

    // Add event handlers
    this.addModalEventHandlers(modal)
  }



  private generateYearOptions(): string {
    const currentYear = new Date().getFullYear()
    const previousYear = currentYear - 1

    return `
      <option value="${currentYear}" selected>${currentYear} (atual)</option>
      <option value="${previousYear}">${previousYear}</option>
    `
  }

  private generateMonthOptions(selectedYear?: number): string {
    const currentDate = new Date()
    const currentMonth = currentDate.getMonth() + 1 // JavaScript months are 0-indexed
    const currentYear = currentDate.getFullYear()

    // Use provided year or default to current year
    const year = selectedYear ?? currentYear

    const months = [
      { value: 1, name: 'Janeiro' },
      { value: 2, name: 'Fevereiro' },
      { value: 3, name: 'Março' },
      { value: 4, name: 'Abril' },
      { value: 5, name: 'Maio' },
      { value: 6, name: 'Junho' },
      { value: 7, name: 'Julho' },
      { value: 8, name: 'Agosto' },
      { value: 9, name: 'Setembro' },
      { value: 10, name: 'Outubro' },
      { value: 11, name: 'Novembro' },
      { value: 12, name: 'Dezembro' }
    ]

    let monthOptions: string[] = []

    // For previous year: show all 12 months
    // For current year: show months up to and including current month
    const maxMonth = year === currentYear ? currentMonth : 12

    for (let i = 1; i <= maxMonth; i++) {
      const monthOption = months.find(m => m.value === i)
      if (monthOption) {
        // Mark current month only if it's the current year
        const isCurrentMonth = year === currentYear && i === currentMonth
        const label = isCurrentMonth ? `${monthOption.name} (atual)` : monthOption.name
        // Pre-select: current month for current year, December for previous year
        const shouldSelect = year === currentYear ? i === currentMonth : i === 12
        const selected = shouldSelect ? ' selected' : ''
        monthOptions.push(`<option value="${monthOption.value}"${selected}>${label}</option>`)
      }
    }

    return monthOptions.join('')
  }

  private addModalEventHandlers(modal: HTMLElement): void {
    const form = modal.querySelector('#route-generation-form') as HTMLFormElement
    const closeBtn = modal.querySelector('#close-modal') as HTMLButtonElement
    const cancelBtn = modal.querySelector('#cancel-btn') as HTMLButtonElement
    const generateBtn = modal.querySelector('#generate-btn') as HTMLButtonElement
    const yearSelect = modal.querySelector('#year') as HTMLSelectElement
    const monthSelect = modal.querySelector('#month') as HTMLSelectElement

    // Update month options when year changes
    yearSelect?.addEventListener('change', () => {
      const selectedYear = parseInt(yearSelect.value)
      monthSelect.innerHTML = this.generateMonthOptions(selectedYear)
    })

    // Flag to prevent multiple close calls
    let isClosing = false

    // Close modal function with immediate removal
    const closeModal = () => {
      if (isClosing) return
      isClosing = true

      // Clean up event listeners
      document.removeEventListener('keydown', handleEscape)

      // Remove modal immediately
      if (modal.parentNode) {
        modal.remove()
      }
    }

    // Close button handler
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      closeModal()
    })

    // Cancel button handler
    cancelBtn.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      closeModal()
    })

    // Click outside to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        e.preventDefault()
        e.stopPropagation()
        closeModal()
      }
    })

    // Escape key to close
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeModal()
      }
    }
    document.addEventListener('keydown', handleEscape)

    // Form submission - ASYNC route generation with progress
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      e.stopPropagation()

      // Prevent multiple submissions
      if (isClosing || generateBtn.disabled) {
        return
      }

      const formData = new FormData(form)
      // Normalize decimal separator: replace comma with dot for parsing
      const targetAmountRaw = (formData.get('target_amount') as string).replace(',', '.')
      const data = {
        target_amount: parseFloat(targetAmountRaw),
        month: parseInt(formData.get('month') as string),
        year: parseInt(formData.get('year') as string)
      }

      // Validate form data
      if (!this.validateRouteForm(data)) {
        return
      }

      // Show loading state
      const btnText = generateBtn.querySelector('.btn-text') as HTMLElement
      const btnLoading = generateBtn.querySelector('.btn-loading') as HTMLElement

      btnText.classList.add('hidden')
      btnLoading.classList.remove('hidden')
      generateBtn.disabled = true

      // Close modal immediately after starting the job
      closeModal()

      // Show progress toast
      const progressToast = this.showProgressToast('A iniciar geração de rotas...')

      try {
        // Call ASYNC API - returns immediately, processes in background
        // This enables scalability for 10-100+ concurrent users
        const result = await routeApi.createGenerationAsync(
          data,
          (progress: number, status: string, stepInfo?: string) => {
            // Update progress toast with current step info
            this.updateGenerationProgressToast(progressToast, progress, status, stepInfo)
          }
        )

        // Show success message
        this.closeProgressToast(progressToast)
        this.showSuccessMessage(
          `Simulação de rotas concluída! ${result.trips_generated} viagens geradas para ${result.colaboradores_count} colaborador(es).`
        )

        // Refresh dashboard data
        setTimeout(() => {
          window.location.reload()
        }, 2000)

      } catch (error) {
        console.error('Failed to create route generation:', error)

        // Close progress toast
        this.closeProgressToast(progressToast)

        // Show error message
        let errorMessage = 'Erro ao simular rotas. Tente novamente.'
        if (error instanceof Error) {
          errorMessage = error.message
        }

        this.showErrorMessage(errorMessage)
      }
    })
  }

  // Update progress toast for route generation with step info
  private updateGenerationProgressToast(toast: HTMLElement, progress: number, status: string, stepInfo?: string): void {
    const progressBar = toast.querySelector('.toast-progress-bar') as HTMLElement
    const percentageSpan = toast.querySelector('.toast-progress-percentage') as HTMLElement
    const messageSpan = toast.querySelector('.toast-message') as HTMLElement

    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
    if (percentageSpan) {
      percentageSpan.textContent = `${progress}%`
    }
    if (messageSpan) {
      // Use step info if available, otherwise use status
      if (stepInfo) {
        messageSpan.textContent = stepInfo
      } else {
        const statusMap: Record<string, string> = {
          'pending': 'Na fila...',
          'processing': 'A gerar rotas...',
          'completed': 'Concluído!'
        }
        messageSpan.textContent = statusMap[status] || 'A processar...'
      }
    }
  }

  private validateRouteForm(data: any): boolean {
    // Validate target amount is a valid number
    if (!data.target_amount || isNaN(data.target_amount)) {
      this.showErrorMessage('Por favor introduza um valor válido em EUR')
      return false
    }

    // Validate target amount range (EUR)
    // Maximum is €4,000 as the hard limit
    // The system will automatically adjust radius, hours, and use weekends if needed
    const MAX_EUR = 4000
    if (data.target_amount < 20 || data.target_amount > MAX_EUR) {
      this.showErrorMessage(`O valor a atingir deve estar entre €20 e €${MAX_EUR.toLocaleString('pt-PT')}`)
      return false
    }

    // Validate that target_amount is a multiple of 0.40 for exact km calculation
    // Use a small epsilon to handle floating-point precision issues
    const remainder = (data.target_amount * 100) % 40
    if (remainder > 0.01 && remainder < 39.99) {
      this.showErrorMessage('O valor deve ser múltiplo de €0,40 para gerar km exatos (ex: €2096.40 = 5241 km)')
      return false
    }

    // Validate month
    if (!data.month || data.month < 1 || data.month > 12) {
      this.showErrorMessage('Selecione um mês válido')
      return false
    }

    // Validate year
    const currentYear = new Date().getFullYear()
    if (!data.year || data.year < currentYear - 1 || data.year > currentYear + 1) {
      this.showErrorMessage('Selecione um ano válido')
      return false
    }

    return true
  }

  private showSuccessMessage(message: string): void {
    this.showToast(message, 'success')
  }

  private showErrorMessage(message: string): void {
    this.showToast(message, 'error')
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    // Remove existing toasts
    document.querySelectorAll('.toast').forEach(toast => toast.remove())

    const toast = createElement('div', {
      className: `toast toast-${type}`
    })

    toast.innerHTML = `
      <div class="toast-content">
        <span class="toast-icon">${type === 'success' ? '✓' : '⚠'}</span>
        <span class="toast-message">${message}</span>
        <button class="toast-close">×</button>
      </div>
    `

    document.body.appendChild(toast)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.remove()
    }, 5000)

    // Close button
    const closeBtn = toast.querySelector('.toast-close') as HTMLButtonElement
    closeBtn.addEventListener('click', () => {
      toast.remove()
    })
  }

  // Progress toast methods for async export
  private showProgressToast(message: string): HTMLElement {
    // Remove any existing progress toasts
    document.querySelectorAll('.toast-progress').forEach(toast => toast.remove())

    const toast = createElement('div', {
      className: 'toast toast-progress'
    })

    toast.innerHTML = `
      <div class="toast-content">
        <span class="toast-icon">
          <i class="fa-solid fa-spinner fa-spin"></i>
        </span>
        <div class="toast-progress-content">
          <span class="toast-message">${message}</span>
          <div class="toast-progress-bar-container">
            <div class="toast-progress-bar" style="width: 0%"></div>
          </div>
          <span class="toast-progress-percentage">0%</span>
        </div>
      </div>
    `

    document.body.appendChild(toast)
    return toast
  }

  private updateProgressToast(toast: HTMLElement, progress: number, status: string): void {
    const progressBar = toast.querySelector('.toast-progress-bar') as HTMLElement
    const percentageSpan = toast.querySelector('.toast-progress-percentage') as HTMLElement
    const messageSpan = toast.querySelector('.toast-message') as HTMLElement

    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
    if (percentageSpan) {
      percentageSpan.textContent = `${progress}%`
    }
    if (messageSpan) {
      const statusMap: Record<string, string> = {
        'pending': 'Na fila...',
        'processing': 'A processar...',
        'completed': 'Concluído!'
      }
      messageSpan.textContent = statusMap[status] || 'A exportar...'
    }
  }

  private closeProgressToast(toast: HTMLElement): void {
    if (toast && toast.parentNode) {
      toast.remove()
    }
  }

  // Utility methods
  private formatCurrency(amount: number): string {
    return amount.toLocaleString('pt-PT', { minimumFractionDigits: 2 })
  }

  private formatKilometers(km: number): string {
    return `${km.toLocaleString('pt-PT')} km`
  }



  private getStatusBadge(status: string): string {
    const statusMap: Record<string, { text: string; class: string }> = {
      'completed': { text: 'Concluído', class: 'status-completed' },
      'processing': { text: 'A processar', class: 'status-processing' },
      'failed': { text: 'Falhado', class: 'status-failed' },
      'pending': { text: 'Pendente', class: 'status-pending' }
    }

    const statusInfo = statusMap[status] || { text: status, class: 'status-default' }
    return `<span class="status-badge ${statusInfo.class}">${statusInfo.text}</span>`
  }

  private formatReportName(report: Report): string {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return `${months[report.month - 1]} ${report.year}`
  }
}
